"use client";

import { Copy, MoveRight, Trash2, X } from "lucide-react";
import { useState } from "react";
import { CitationClusterButton } from "@/components/citation-cluster-composer";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Bibliography } from "@/lib/bibliography";
import { t } from "@/lib/i18n/interpolate";

interface SelectionBarProps {
  count: number;
  /** Other projects the selection can be copied or moved into. */
  targets: Bibliography[];
  onClear: () => void;
  onCluster: () => void;
  onTransfer: (targetId: string, mode: "copy" | "move") => void;
  onDelete: () => void;
}

/**
 * Floating bulk-action bar that appears once anything is checked (the ClickUp /
 * Scribbr pattern): cite together, copy or move to another project, delete.
 */
export function SelectionBar({
  count,
  targets,
  onClear,
  onCluster,
  onTransfer,
  onDelete,
}: SelectionBarProps) {
  const bib = useBibStrings();
  const [targetId, setTargetId] = useState("");

  if (count === 0) {
    return null;
  }

  return (
    <div
      aria-live="polite"
      className="sticky bottom-4 z-20 mx-auto flex w-fit max-w-full flex-wrap items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-2 shadow-soft-lg"
      role="toolbar"
    >
      <span className="px-1 font-medium text-sm tabular-nums">
        {t(bib.selectedCount, { count })}
      </span>
      <Button
        onClick={onClear}
        size="icon-sm"
        title={bib.studioClearSelection}
        variant="ghost"
      >
        <X className="size-4" />
        <span className="sr-only">{bib.studioClearSelection}</span>
      </Button>
      <span aria-hidden="true" className="h-5 w-px bg-border" />
      <CitationClusterButton disabled={count < 2} onClick={onCluster} />
      {targets.length > 0 ? (
        <>
          <Select onValueChange={setTargetId} value={targetId}>
            <SelectTrigger
              aria-label={bib.destination}
              className="h-9 w-44"
              size="sm"
            >
              <SelectValue placeholder={bib.chooseDestination} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            disabled={!targetId}
            onClick={() => onTransfer(targetId, "copy")}
            size="icon-sm"
            title={bib.studioCopyTo}
            variant="outline"
          >
            <Copy className="size-4" />
            <span className="sr-only">{bib.copySelected}</span>
          </Button>
          <Button
            disabled={!targetId}
            onClick={() => onTransfer(targetId, "move")}
            size="icon-sm"
            title={bib.studioMoveTo}
            variant="outline"
          >
            <MoveRight className="size-4" />
            <span className="sr-only">{bib.moveSelected}</span>
          </Button>
        </>
      ) : null}
      <Button
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={onDelete}
        size="sm"
        variant="ghost"
      >
        <Trash2 className="size-4" />
        {bib.studioDeleteSelected}
      </Button>
    </div>
  );
}
